import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

// Enable httpOnly cookie auth
axios.defaults.withCredentials = true;
import { 
  BookOpen,
  PieChart, 
  TrendingUp, 
  CreditCard, 
  Shield, 
  ArrowRight,
  Check,
  BarChart3,
  FileText,
  Loader2,
  Sparkles,
  Building2,
  Receipt,
  Eye,
  EyeOff,
  History,
  Upload,
  Clock,
  Mail,
  TrendingDown,
  Target,
  AlertTriangle,
  Quote
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Check if we're on MyBookie domain for clean URLs
const isMyBookieDomain = () => {
  const hostname = window.location.hostname;
  return !hostname.includes('repti-sitter') && !hostname.includes('reptisitter');
};

const getDashboardPath = () => {
  return isMyBookieDomain() ? '/dashboard' : '/mybookie/dashboard';
};

const MyBookieLanding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  // Where to return the user if they close the auth dialog without signing up
  // (set when they arrive from /pricing or /cfo via ?signup=1&from=...)
  const [authReturnPath, setAuthReturnPath] = useState(null);

  // Show session expired toast if redirected from dashboard, or auto-open signup
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth') === 'expired') {
      toast({ title: "Session expired", description: "Please log in again.", variant: "destructive" });
      window.history.replaceState({}, '', window.location.pathname);
      setShowAuth(true);
    } else if (params.get('signup') === '1') {
      // User clicked a Pricing CTA — auto-open the auth dialog on "Sign up" tab
      setIsLogin(false);
      setShowAuth(true);
      const from = params.get('from');
      if (from) {
        setAuthReturnPath(`/${from.replace(/^\/+/, '')}`);
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotStep, setForgotStep] = useState('email'); // 'email' | 'token'
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFAEmail, setTwoFAEmail] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [resending2FA, setResending2FA] = useState(false);
  const [showDemoSignup, setShowDemoSignup] = useState(false);
  const [demoEmail, setDemoEmail] = useState('');
  const [demoCreating, setDemoCreating] = useState(false);
  const [demoCreated, setDemoCreated] = useState(false);
  const [demoPassword, setDemoPassword] = useState('');

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/api/mybookie/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        
        // Check if 2FA is required
        if (response.data.requires_2fa) {
          setTwoFAEmail(response.data.email);
          setTwoFACode('');
          setShow2FA(true);
          toast({ title: "Verification code sent", description: "Check your email for the 6-digit code" });
          return;
        }
        
        // httpOnly cookie set by backend automatically
        // Also store token in sessionStorage as fallback for cross-origin deployments
        if (response.data.access_token) {
          sessionStorage.setItem('mybookie_token', response.data.access_token);
        }
        localStorage.setItem('mybookie_user', JSON.stringify(response.data.user));
        setShowAuth(false);
        toast({ title: "Welcome back!", description: "Logged in successfully" });
        navigate(getDashboardPath());
      } else {
        const response = await axios.post(`${API}/api/mybookie/auth/register`, {
          email: formData.email,
          password: formData.password,
          name: formData.name
        });
        
        // httpOnly cookie set by backend automatically
        if (response.data.access_token) {
          sessionStorage.setItem('mybookie_token', response.data.access_token);
        }
        localStorage.setItem('mybookie_user', JSON.stringify(response.data.user));
        setShowAuth(false);
        toast({ title: "Account created!", description: "Welcome to MyBookie" });
        navigate(getDashboardPath());
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Authentication failed",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/mybookie/auth/verify-2fa`, {
        email: twoFAEmail,
        code: twoFACode
      });
      if (response.data.access_token) {
        sessionStorage.setItem('mybookie_token', response.data.access_token);
      }
      localStorage.setItem('mybookie_user', JSON.stringify(response.data.user));
      setShow2FA(false);
      setShowAuth(false);
      toast({ title: "Welcome back!", description: "Logged in successfully" });
      navigate(getDashboardPath());
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Invalid code",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResend2FA = async () => {
    setResending2FA(true);
    try {
      await axios.post(`${API}/api/mybookie/auth/resend-2fa`, { email: twoFAEmail });
      toast({ title: "Code resent", description: "Check your email for the new code" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to resend code", variant: "destructive" });
    } finally {
      setResending2FA(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/mybookie/auth/forgot-password`, { email: forgotEmail });
      setResetToken(response.data.reset_token || '');
      setForgotStep('token');
      toast({ title: "Check your email", description: "A reset code has been sent to your email" });
    } catch (err) {
      console.error("Password reset error:", err);
      toast({ title: "Error", description: "Something went wrong", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast({ title: "Error", description: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/mybookie/auth/reset-password`, { token: resetToken, new_password: newPassword });
      toast({ title: "Password Reset!", description: "You can now log in with your new password" });
      setShowForgotPassword(false);
      setForgotStep('email');
      setIsLogin(true);
      setShowAuth(true);
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Invalid or expired token", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const [demoLoading, setDemoLoading] = useState(false);

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    try {
      // First seed the demo account (idempotent)
      await axios.post(`${API}/api/mybookie/seed-demo`);
      // Then login
      const response = await axios.post(`${API}/api/mybookie/auth/demo-login`);
      localStorage.setItem('mybookie_user', JSON.stringify(response.data.user));
      toast({ title: "Welcome to the demo!", description: "Explore Sunrise Coffee Co.'s books" });
      navigate(isMyBookieDomain() ? '/dashboard' : '/mybookie/dashboard');
    } catch (err) {
      console.error("Demo login error:", err);
      toast({ title: "Error", description: "Could not load demo", variant: "destructive" });
    } finally {
      setDemoLoading(false);
    }
  };

  const handleCreateDemoAccount = async (e) => {
    e.preventDefault();
    setDemoCreating(true);
    try {
      const response = await axios.post(`${API}/api/mybookie/auth/create-demo`, {
        email: demoEmail
      });
      setDemoPassword(response.data.demo_password);
      setDemoCreated(true);
      localStorage.setItem('mybookie_user', JSON.stringify(response.data.user));
      toast({ title: "Demo account created!", description: "Check your email for login details" });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Could not create demo account",
        variant: "destructive"
      });
    } finally {
      setDemoCreating(false);
    }
  };

  const handleDemoGoToDashboard = () => {
    setShowDemoSignup(false);
    setDemoCreated(false);
    setDemoEmail('');
    setDemoPassword('');
    navigate(getDashboardPath());
  };

  const features = [
    {
      icon: <CreditCard className="w-5 h-5" />,
      title: "Bank Connection",
      description: "Securely connect 12,000+ banks and auto-import transactions via Plaid (production)."
    },
    {
      icon: <Sparkles className="w-5 h-5" />,
      title: "Smart Auto-Categorization",
      description: "Vendor-learning AI gets smarter each time you correct a category. Bulk re-categorize 80 transactions in 2 clicks."
    },
    {
      icon: <TrendingDown className="w-5 h-5" />,
      title: "Runway & Burn Rate",
      description: "Real-time CFO view: how many months your cash will last, monthly burn trend, vs prior month comparison."
    },
    {
      icon: <Target className="w-5 h-5" />,
      title: "Budget vs Actuals",
      description: "Set monthly category budgets, track variance with color-coded bars. Catch overspend before month-end."
    },
    {
      icon: <AlertTriangle className="w-5 h-5" />,
      title: "Anomaly Detection",
      description: "Catches duplicate charges, vendor spending spikes >50%, and unusually large new-vendor payments automatically."
    },
    {
      icon: <BarChart3 className="w-5 h-5" />,
      title: "Cash Flow Forecast",
      description: "Project income vs expenses weeks ahead based on recurring items."
    },
    {
      icon: <FileText className="w-5 h-5" />,
      title: "Invoicing & Tax Forms",
      description: "Send invoices with automated reminders. Generate W-2 and 1099-NEC at tax time."
    },
    {
      icon: <Building2 className="w-5 h-5" />,
      title: "Multi-Business",
      description: "Track multiple ventures in one place — real estate, e-commerce, consulting, freelance."
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "SOC 2 Security",
      description: "SOC 2 Type I/II compliant infrastructure. Daily automated audits, 2FA, account lockout, full audit logging."
    }
  ];

  const testimonials = [
    {
      quote: "I went from a shoebox of receipts to a real P&L in 20 minutes. The runway tracker alone is worth it.",
      author: "Marcus T.",
      role: "Real estate investor · Business plan"
    },
    {
      quote: "Connected my Chase and credit union accounts on a Sunday. By Monday morning I had 4 months of transactions categorized.",
      author: "Sarah K.",
      role: "Solo consultant · Solo plan"
    },
    {
      quote: "The anomaly detection caught a $1,200 duplicate vendor charge I would've missed for weeks.",
      author: "David R.",
      role: "E-commerce founder · Business+ plan"
    }
  ];

  const pricingFeatures = [
    "Unlimited businesses",
    "Bank account connections",
    "Auto-import & categorize transactions",
    "CFO Insights (runway, burn, budgets, anomalies)",
    "P&L reports & cash flow forecasting",
    "Invoicing with automated reminders",
    "Receipt scanning & mileage tracking",
    "Multi-currency support",
    "W-2 / 1099-NEC tax form generation",
    "Payroll with progressive state brackets",
    "Client Portal & accountant sharing"
  ];

  return (
    <div className="min-h-screen bg-[#F6F5F2]">
      {/* Navigation */}
      <nav className="bg-white border-b border-[#E6E8E6] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0A2B1D] rounded-sm flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-[#111614] tracking-tight">MyBookie</span>
          </div>
          <div className="flex items-center gap-4">
            <a 
              href={isMyBookieDomain() ? '/pricing' : '/mybookie/pricing'}
              className="text-sm text-[#5C6661] hover:text-[#111614] transition-colors hidden sm:block"
            >
              Pricing
            </a>
            <Button 
              variant="ghost" 
              className="text-[#5C6661] hover:text-[#111614] hover:bg-[#E2ECE7]"
              onClick={() => { setIsLogin(true); setShowAuth(true); }}
              data-testid="nav-login-btn"
            >
              Log In
            </Button>
            <Button 
              onClick={() => { setIsLogin(false); setShowAuth(true); }} 
              className="bg-[#0A2B1D] hover:bg-[#254D3D] text-white rounded-sm"
              data-testid="nav-get-started-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A2B1D] via-[#254D3D] to-[#0A2B1D]" />
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23B4C9BE' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-sm text-sm text-[#B4C9BE] mb-8">
            <Sparkles className="w-4 h-4" />
            <span>AI bookkeeping + CFO insights for solopreneurs &amp; startups</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white mb-6 leading-tight tracking-tight">
            Your books, automated.<br />
            <span className="text-[#B4C9BE]">Your runway, in real time.</span>
          </h1>
          
          <p className="text-lg md:text-xl text-[#B4C9BE] mb-10 max-w-2xl mx-auto leading-relaxed">
            Connect your bank, auto-categorize transactions, and get instant CFO-level insights — runway, burn rate, budget tracking, and anomaly alerts. Built for entrepreneurs who'd rather grow than do paperwork.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-[#0A2B1D] hover:bg-[#E2ECE7] rounded-sm px-8 py-6 text-base font-medium"
              onClick={() => { setIsLogin(false); setShowAuth(true); }}
              data-testid="hero-start-trial-btn"
            >
              Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-[#B4C9BE]/30 text-white hover:bg-white/10 rounded-sm px-8 py-6 text-base"
              onClick={() => setShowDemoSignup(true)}
              data-testid="hero-try-demo-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Try Free Demo
            </Button>
          </div>
          
          <p className="text-[#8A948F] text-sm mt-6">No credit card required • 30-day free demo</p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-b border-[#E6E8E6]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-semibold text-[#0A2B1D]" data-testid="stat-businesses">100+</div>
              <div className="text-[#5C6661] text-sm mt-1">Businesses on MyBookie</div>
            </div>
            <div>
              <div className="text-3xl font-semibold text-[#0A2B1D]">12,000+</div>
              <div className="text-[#5C6661] text-sm mt-1">Banks Supported</div>
            </div>
            <div>
              <div className="text-3xl font-semibold text-[#0A2B1D]">5 min</div>
              <div className="text-[#5C6661] text-sm mt-1">Setup Time</div>
            </div>
            <div>
              <div className="text-3xl font-semibold text-[#0A2B1D]">256-bit</div>
              <div className="text-[#5C6661] text-sm mt-1">Encryption</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-medium mb-4">Features</p>
            <h2 className="text-3xl md:text-4xl font-medium text-[#111614] mb-4 tracking-tight">
              Everything you need to manage business finances
            </h2>
            <p className="text-lg text-[#5C6661] max-w-2xl mx-auto">
              Stop wasting time on spreadsheets. MyBookie automates the tedious parts so you can focus on what matters.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.title} 
                className="bg-white p-8 border border-[#E6E8E6] hover:border-[#B4C9BE] hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-10 h-10 bg-[#E2ECE7] flex items-center justify-center text-[#0A2B1D] mb-5">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-medium text-[#111614] mb-2">{feature.title}</h3>
                <p className="text-[#5C6661] text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CFO Mode Spotlight — links to /cfo */}
      <section className="py-20 px-6 bg-[#0A2B1D] text-white" data-testid="landing-cfo-spotlight">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1 rounded-sm text-xs text-[#B4C9BE] mb-4">
                <Sparkles className="w-3.5 h-3.5" /> Exclusive · CFO Mode
              </div>
              <h2 className="text-3xl md:text-4xl font-medium mb-4 tracking-tight">
                Most bookkeeping apps tell you<br /><span className="text-[#B4C9BE]">what happened.</span>
              </h2>
              <p className="text-2xl md:text-3xl font-medium mb-6">We tell you <span className="underline decoration-[#B4C9BE]">what to do.</span></p>
              <p className="text-[#B4C9BE] mb-8 leading-relaxed">
                Runway · Burn rate · Budget vs Actuals · Anomaly detection · Scenario planning · AI commentary · Weekly Finance Brief. Out-of-the-box CFO insights you won't find in any other bookkeeping app.
              </p>
              <Button
                onClick={() => navigate('/cfo')}
                className="bg-white text-[#0A2B1D] hover:bg-[#B4C9BE] rounded-sm h-11 px-6"
                data-testid="landing-cfo-cta"
              >
                Explore CFO Mode <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: <TrendingDown className="w-5 h-5" />, label: 'Runway & Burn' },
                { icon: <Target className="w-5 h-5" />, label: 'Budget vs Actuals' },
                { icon: <AlertTriangle className="w-5 h-5" />, label: 'Anomaly Detection' },
                { icon: <Sparkles className="w-5 h-5" />, label: 'AI Commentary' },
              ].map((c, idx) => (
                <div key={idx} className="bg-white/5 border border-white/10 rounded-md p-4">
                  <div className="text-[#B4C9BE] mb-2">{c.icon}</div>
                  <div className="text-sm font-medium text-white">{c.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-medium mb-4">How It Works</p>
            <h2 className="text-3xl md:text-4xl font-medium text-[#111614] tracking-tight">
              Get started in three simple steps
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-14 h-14 bg-[#0A2B1D] text-white flex items-center justify-center text-xl font-medium mx-auto mb-6">
                1
              </div>
              <h3 className="text-lg font-medium text-[#111614] mb-3">Create Your Account</h3>
              <p className="text-[#5C6661] text-sm leading-relaxed">Sign up in seconds. Add your businesses - real estate, e-commerce, consulting, or any type.</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#0A2B1D] text-white flex items-center justify-center text-xl font-medium mx-auto mb-6">
                2
              </div>
              <h3 className="text-lg font-medium text-[#111614] mb-3">Connect Your Banks</h3>
              <p className="text-[#5C6661] text-sm leading-relaxed">Securely link your business bank accounts via Plaid. Transactions sync automatically.</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#0A2B1D] text-white flex items-center justify-center text-xl font-medium mx-auto mb-6">
                3
              </div>
              <h3 className="text-lg font-medium text-[#111614] mb-3">Get CFO Insights</h3>
              <p className="text-[#5C6661] text-sm leading-relaxed">Track runway, burn rate, budget variance, and anomalies. Generate tax-ready reports and share with your accountant.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials / Trust Signals */}
      <section className="py-24 px-6 bg-[#F4F1EA]" data-testid="landing-testimonials">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-medium mb-4">Why founders trust MyBookie</p>
            <h2 className="text-3xl md:text-4xl font-medium text-[#111614] tracking-tight">
              Built by entrepreneurs, for entrepreneurs
            </h2>
            <p className="text-[#5C6661] mt-3 max-w-2xl mx-auto">
              Real feedback from real users running real businesses. Connect your bank in under 3 minutes — no credit card required.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, idx) => (
              <div
                key={idx}
                className="bg-white border border-[#E6E8E6] rounded-md p-6"
                data-testid={`landing-testimonial-${idx}`}
              >
                <Quote className="w-5 h-5 text-[#0A2B1D] mb-3 opacity-40" />
                <p className="text-[#111614] text-sm leading-relaxed mb-4">&ldquo;{t.quote}&rdquo;</p>
                <div className="text-xs text-[#5C6661]">
                  <div className="font-medium text-[#111614]">{t.author}</div>
                  <div className="mt-0.5">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-[#5C6661]">
            <span className="inline-flex items-center gap-1.5 font-medium" data-testid="trust-100-plus"><Sparkles className="w-4 h-4 text-[#0A2B1D]" /> 100+ entrepreneurs trust MyBookie</span>
            <span className="inline-flex items-center gap-1.5"><Shield className="w-4 h-4" /> SOC 2 Type I/II compliant</span>
            <span className="inline-flex items-center gap-1.5"><CreditCard className="w-4 h-4" /> Plaid Production · 12,000+ banks</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-600" /> Bank-level encryption</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-600" /> No credit card to start</span>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-medium mb-4">Pricing</p>
            <h2 className="text-3xl md:text-4xl font-medium text-[#111614] mb-4 tracking-tight">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-[#5C6661]">
              Start free. Upgrade when you're ready.
            </p>
          </div>
          
          <div className="bg-white border-2 border-[#0A2B1D] p-10 md:p-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#0A2B1D] font-medium mb-2">Pro Plan</div>
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-medium text-[#111614]">$19</span>
                  <span className="text-[#5C6661]">/month</span>
                </div>
                <p className="text-[#5C6661] mt-2 text-sm">Everything you need to manage business finances</p>
              </div>
              <Button 
                size="lg" 
                className="bg-[#0A2B1D] hover:bg-[#254D3D] text-white rounded-sm mt-6 md:mt-0 px-8"
                onClick={() => { setIsLogin(false); setShowAuth(true); }}
                data-testid="pricing-cta-btn"
              >
                Start 14-Day Free Trial
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {pricingFeatures.map((feature, index) => (
                <div key={feature} className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-[#E2ECE7] flex items-center justify-center">
                    <Check className="w-3 h-3 text-[#3A6E53]" />
                  </div>
                  <span className="text-[#5C6661] text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Back on Track Section */}
      <section className="py-24 px-6 bg-white border-b border-[#E6E8E6]" data-testid="backontrack-landing-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-[#E2ECE7] px-3 py-1.5 rounded-sm text-xs font-medium text-[#0A2B1D] mb-6">
                <History className="w-3.5 h-3.5" />
                Premium Add-On
              </div>
              <h2 className="text-3xl md:text-4xl font-medium text-[#111614] mb-4 tracking-tight">
                Behind on your books?<br />
                <span className="text-[#3A6E53]">Get back on track.</span>
              </h2>
              <p className="text-[#5C6661] leading-relaxed mb-6">
                Whether you're 1 year or 5 years behind, our AI-powered catch-up service 
                transforms your bank statements into clean, categorized financials — ready 
                for taxes, investors, or just peace of mind.
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  { icon: <Upload className="w-4 h-4" />, text: 'Upload your bank CSVs — we handle the rest' },
                  { icon: <Sparkles className="w-4 h-4" />, text: 'AI auto-categorizes every transaction' },
                  { icon: <FileText className="w-4 h-4" />, text: 'Instant P&L reports for each year' },
                  { icon: <Clock className="w-4 h-4" />, text: 'Done in minutes, not months' }
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#E2ECE7] flex items-center justify-center text-[#0A2B1D]">
                      {item.icon}
                    </div>
                    <span className="text-sm text-[#111614]">{item.text}</span>
                  </div>
                ))}
              </div>

              <Button 
                size="lg"
                className="bg-[#0A2B1D] hover:bg-[#254D3D] text-white rounded-sm px-8"
                onClick={() => { setIsLogin(false); setShowAuth(true); }}
                data-testid="backontrack-cta-btn"
              >
                Get Started <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>

            {/* Right - Pricing Card */}
            <div className="bg-[#F6F5F2] border border-[#E6E8E6] p-8">
              <div className="text-center mb-6">
                <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-medium mb-3">Back on Track</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-medium text-[#111614]">$350</span>
                  <span className="text-[#5C6661]">/year</span>
                </div>
                <p className="text-sm text-[#5C6661] mt-2">Per year of catch-up bookkeeping</p>
              </div>
              
              <div className="border-t border-[#E6E8E6] pt-6 space-y-3">
                {[
                  'Upload CSV bank statements',
                  'AI-powered transaction categorization',
                  'Income & expense separation',
                  'P&L report generation',
                  'Tax-ready financial data',
                  'Select 1-5 years to catch up'
                ].map((feat) => (
                  <div key={feat} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-[#E2ECE7] flex items-center justify-center">
                      <Check className="w-3 h-3 text-[#3A6E53]" />
                    </div>
                    <span className="text-sm text-[#5C6661]">{feat}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 bg-[#0A2B1D] text-white p-4 text-center">
                <p className="text-sm font-medium">Example: 2 years behind?</p>
                <p className="text-2xl font-semibold mt-1">$700 <span className="text-sm font-normal text-[#9CC4AF]">one-time</span></p>
                <p className="text-xs text-[#9CC4AF] mt-1">That's less than $60/month spread over a year</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-[#0A2B1D]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-medium text-white mb-4 tracking-tight">
            Ready to simplify your bookkeeping?
          </h2>
          <p className="text-lg text-[#B4C9BE] mb-10">
            Join entrepreneurs who trust MyBookie for their business finances.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-[#0A2B1D] hover:bg-[#E2ECE7] rounded-sm px-8 py-6 text-base font-medium"
            onClick={() => { setIsLogin(false); setShowAuth(true); }}
            data-testid="cta-get-started-btn"
          >
            Get Started Free <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#111614] text-[#8A948F] py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3 mb-4 md:mb-0">
              <div className="w-8 h-8 bg-[#254D3D] rounded-sm flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="text-base font-medium text-white">MyBookie</span>
            </div>
            <div className="flex gap-6 text-sm flex-wrap">
              <a href={isMyBookieDomain() ? '/pricing' : '/mybookie/pricing'} className="hover:text-white transition-colors">Pricing</a>
              <a href="/mybookie-competitive-analysis.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-testid="competitive-analysis-link">Why MyBookie?</a>
              <a href={isMyBookieDomain() ? '/privacy' : '/mybookie/privacy'} className="hover:text-white transition-colors">Privacy</a>
              <a href={isMyBookieDomain() ? '/terms' : '/mybookie/terms'} className="hover:text-white transition-colors">Terms</a>
              <a href="mailto:support@mybookie.ai" className="hover:text-white transition-colors">Support</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-[#254D3D] flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
            <div>© 2026 MyBookie. All rights reserved.</div>
            <Link to="/security" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#0A2B1D] border border-[#254D3D] hover:border-[#9CC4AF] transition" data-testid="security-grade-badge" title="View live security posture & compliance">
              <Shield className="w-3.5 h-3.5 text-[#9CC4AF]" />
              <span className="text-xs text-[#B4C9BE]">Security audit:</span>
              <span className="text-xs font-bold text-emerald-300">A+</span>
            </Link>
          </div>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={(open) => {
        setShowAuth(open);
        if (!open) {
          setShow2FA(false);
          setTwoFACode('');
          // If the dialog was launched from /pricing or /cfo, bounce back so the
          // user isn't stranded on the homepage after dismissing.
          if (authReturnPath) {
            const path = authReturnPath;
            setAuthReturnPath(null);
            navigate(path);
          }
        }
      }}>
        <DialogContent className="sm:max-w-md bg-white border-[#E6E8E6]">
          {show2FA ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#111614]">Two-Factor Verification</DialogTitle>
                <DialogDescription className="text-[#5C6661]">
                  Enter the 6-digit code sent to {twoFAEmail}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleVerify2FA} className="space-y-4 pt-4">
                <div>
                  <Label className="text-[#111614]">Verification Code</Label>
                  <Input
                    type="text"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    required
                    maxLength={6}
                    className="bg-[#FAFAF8] border-[#E6E8E6] text-center text-2xl tracking-[0.5em] font-mono"
                    data-testid="2fa-code-input"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#0A2B1D] hover:bg-[#254D3D] text-white rounded-sm"
                  disabled={loading || twoFACode.length !== 6}
                  data-testid="2fa-verify-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & Log In'}
                </Button>
                <div className="text-center text-sm text-[#5C6661]">
                  Didn't receive the code?{' '}
                  <button
                    type="button"
                    onClick={handleResend2FA}
                    disabled={resending2FA}
                    className="text-[#0A2B1D] hover:underline font-medium"
                    data-testid="resend-2fa-btn"
                  >
                    {resending2FA ? 'Sending...' : 'Resend'}
                  </button>
                </div>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setShow2FA(false); setTwoFACode(''); }}
                    className="text-xs text-[#5C6661] hover:text-[#0A2B1D] hover:underline"
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#111614]">{isLogin ? 'Welcome Back' : 'Create Account'}</DialogTitle>
                <DialogDescription className="text-[#5C6661]">
                  {isLogin ? 'Log in to your MyBookie account' : 'Start tracking your business finances'}
                </DialogDescription>
              </DialogHeader>
          
              <form onSubmit={handleAuth} className="space-y-4 pt-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-[#111614]">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Smith"
                  required={!isLogin}
                  className="bg-[#FAFAF8] border-[#E6E8E6] focus:ring-[#B4C9BE] focus:border-[#B4C9BE]"
                  data-testid="auth-name-input"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="email" className="text-[#111614]">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="you@example.com"
                required
                className="bg-[#FAFAF8] border-[#E6E8E6] focus:ring-[#B4C9BE] focus:border-[#B4C9BE]"
                data-testid="auth-email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-[#111614]">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••"
                  required
                  className="bg-[#FAFAF8] border-[#E6E8E6] focus:ring-[#B4C9BE] focus:border-[#B4C9BE] pr-10"
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6661] hover:text-[#111614] transition-colors"
                  data-testid="toggle-password-visibility"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-[#0A2B1D] hover:bg-[#254D3D] text-white rounded-sm" 
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                isLogin ? 'Log In' : 'Create Account'
              )}
            </Button>
            
            <div className="text-center text-sm text-[#5C6661]">
              {isLogin ? (
                <>
                  <button 
                    type="button" 
                    onClick={() => { setShowAuth(false); setShowForgotPassword(true); setForgotStep('email'); }}
                    className="text-[#5C6661] hover:text-[#0A2B1D] hover:underline text-xs mb-3 block mx-auto"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                  Don't have an account?{' '}
                  <button type="button" onClick={() => setIsLogin(false)} className="text-[#0A2B1D] hover:underline font-medium">
                    Sign up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button type="button" onClick={() => setIsLogin(true)} className="text-[#0A2B1D] hover:underline font-medium">
                    Log in
                  </button>
                </>
              )}
            </div>
          </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md bg-white border-[#E6E8E6]" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#111614]">
              {forgotStep === 'email' ? 'Reset Password' : 'Enter New Password'}
            </DialogTitle>
            <DialogDescription className="text-[#5C6661]">
              {forgotStep === 'email' 
                ? "Enter your email and we'll send you a reset code" 
                : "Enter the reset token and your new password"}
            </DialogDescription>
          </DialogHeader>
          
          {forgotStep === 'email' ? (
            <form onSubmit={handleForgotPassword} className="space-y-4 pt-4">
              <div>
                <Label className="text-[#111614]">Email</Label>
                <Input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="bg-[#FAFAF8] border-[#E6E8E6]"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" 
                disabled={loading}
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Reset Code'}
              </Button>
              <button 
                type="button" 
                onClick={() => { setShowForgotPassword(false); setIsLogin(true); setShowAuth(true); }}
                className="w-full text-center text-sm text-[#5C6661] hover:text-[#0A2B1D]"
              >
                Back to Login
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4 pt-4">
              <div>
                <Label className="text-[#111614]">Reset Token</Label>
                <Input
                  value={resetToken}
                  onChange={(e) => setResetToken(e.target.value)}
                  placeholder="Paste your reset token"
                  required
                  className="bg-[#FAFAF8] border-[#E6E8E6]"
                  data-testid="reset-token-input"
                />
              </div>
              <div>
                <Label className="text-[#111614]">New Password</Label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    className="bg-[#FAFAF8] border-[#E6E8E6] pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6661] hover:text-[#111614] transition-colors"
                    data-testid="toggle-new-password-visibility"
                    tabIndex={-1}
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" 
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Demo Signup Dialog */}
      <Dialog open={showDemoSignup} onOpenChange={(open) => { if (!open) { setDemoCreated(false); setDemoEmail(''); setDemoPassword(''); } setShowDemoSignup(open); }}>
        <DialogContent className="sm:max-w-md bg-white border-[#E6E8E6]" data-testid="demo-signup-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#111614]">
              {demoCreated ? 'Your Demo is Ready!' : 'Try MyBookie Free'}
            </DialogTitle>
            <DialogDescription className="text-[#5C6661]">
              {demoCreated 
                ? 'Your 30-day demo account has been created' 
                : 'Enter your email to get a free 30-day demo account'}
            </DialogDescription>
          </DialogHeader>
          
          {demoCreated ? (
            <div className="space-y-4 pt-4">
              <div className="bg-[#E2ECE7] rounded-lg p-4">
                <p className="text-sm font-medium text-[#0A2B1D] mb-3">Your Login Credentials</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between bg-white rounded px-3 py-2">
                    <span className="text-sm text-[#5C6661]">Email</span>
                    <span className="text-sm font-medium text-[#111614]" data-testid="demo-email-display">{demoEmail}</span>
                  </div>
                  <div className="flex items-center justify-between bg-white rounded px-3 py-2">
                    <span className="text-sm text-[#5C6661]">Password</span>
                    <span className="text-sm font-mono font-medium text-[#111614]" data-testid="demo-password-display">{demoPassword}</span>
                  </div>
                </div>
                <p className="text-xs text-[#5C6661] mt-3">These credentials have been sent to your email too.</p>
              </div>
              
              <div className="bg-[#F6F5F2] rounded-lg p-4 text-sm text-[#5C6661]">
                <p className="font-medium text-[#111614] mb-1">What you can do:</p>
                <ul className="space-y-1 text-xs">
                  <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#3A6E53]" /> Create businesses & track expenses</li>
                  <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#3A6E53]" /> Generate invoices & P&L reports</li>
                  <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#3A6E53]" /> Use AI financial assistant</li>
                  <li className="flex items-center gap-2"><Check className="w-3 h-3 text-[#3A6E53]" /> Scan receipts & track mileage</li>
                </ul>
              </div>

              <Button
                onClick={handleDemoGoToDashboard}
                className="w-full bg-[#0A2B1D] hover:bg-[#254D3D] text-white rounded-sm"
                data-testid="demo-go-to-dashboard-btn"
              >
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ) : (
            <form onSubmit={handleCreateDemoAccount} className="space-y-4 pt-4">
              <div>
                <Label className="text-[#111614]">Email Address</Label>
                <Input
                  type="email"
                  value={demoEmail}
                  onChange={(e) => setDemoEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="bg-[#FAFAF8] border-[#E6E8E6]"
                  data-testid="demo-email-input"
                  autoFocus
                />
                <p className="text-xs text-[#8A948F] mt-1.5">We'll send your login credentials to this email</p>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#0A2B1D] hover:bg-[#254D3D] text-white rounded-sm"
                disabled={demoCreating}
                data-testid="demo-create-btn"
              >
                {demoCreating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                {demoCreating ? 'Creating...' : 'Create Demo Account'}
              </Button>
              <div className="text-center text-xs text-[#8A948F]">
                <p>30-day free trial • No credit card • Full access</p>
              </div>
              <div className="text-center text-sm text-[#5C6661]">
                Already have an account?{' '}
                <button type="button" onClick={() => { setShowDemoSignup(false); setIsLogin(true); setShowAuth(true); }} className="text-[#0A2B1D] hover:underline font-medium">
                  Log in
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyBookieLanding;
